import { DropdownMenu as M, Popover as P, Tooltip as T } from 'radix-ui';
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

// Every floating panel renders in a portal (so no overflow-hidden or scrolling parent clips it),
// flips to the other side and shifts along its edge to stay on screen (Radix collision handling,
// 8px from the viewport), and is never taller or wider than the space Radix measures: long
// content scrolls inside it.
const COLLISION_PADDING = 8;
const popClass = 'z-50 min-w-44 rounded-lg border bg-surface p-1 text-sm shadow-pop data-[state=open]:animate-fade-in';

export function Menu({ trigger, children, align = 'end' }: { trigger: ReactNode; children: ReactNode; align?: 'start' | 'end' | 'center' }) {
  return (
    <M.Root>
      <M.Trigger asChild>{trigger}</M.Trigger>
      <M.Portal>
        <M.Content
          align={align}
          sideOffset={4}
          collisionPadding={COLLISION_PADDING}
          className={cn(popClass, 'max-h-[var(--radix-dropdown-menu-content-available-height)] max-w-[var(--radix-dropdown-menu-content-available-width)] overflow-y-auto overscroll-contain')}
        >
          {children}
        </M.Content>
      </M.Portal>
    </M.Root>
  );
}

export function MenuItem({ children, onSelect, danger, icon }: { children: ReactNode; onSelect?: () => void; danger?: boolean; icon?: ReactNode }) {
  return (
    <M.Item
      onSelect={onSelect}
      className={cn(
        'flex cursor-pointer select-none items-center gap-2 rounded-md px-2 py-1.5 outline-none data-[highlighted]:bg-surface-hover [&_svg]:size-4 [&_svg]:text-subtle',
        danger && 'text-danger [&_svg]:text-danger',
      )}
    >
      {icon}
      {children}
    </M.Item>
  );
}

export const MenuLabel = ({ children }: { children: ReactNode }) => <M.Label className="px-2 py-1 text-xs font-medium text-subtle">{children}</M.Label>;
export const MenuSeparator = () => <M.Separator className="my-1 h-px bg-border" />;

export function Popover({
  trigger,
  children,
  className,
  align = 'start',
  side,
  open,
  onOpenChange,
}: {
  trigger: ReactNode;
  children: ReactNode;
  className?: string;
  align?: 'start' | 'end' | 'center';
  side?: 'top' | 'right' | 'bottom' | 'left';
  open?: boolean;
  onOpenChange?: (o: boolean) => void;
}) {
  return (
    <P.Root open={open} onOpenChange={onOpenChange}>
      <P.Trigger asChild>{trigger}</P.Trigger>
      <P.Portal>
        <P.Content
          align={align}
          side={side}
          sideOffset={4}
          collisionPadding={COLLISION_PADDING}
          className={cn(
            popClass,
            'p-0',
            className,
            // Last, so a fixed width (w-96) still gives way on a narrow screen.
            'max-h-[var(--radix-popover-content-available-height)] max-w-[min(var(--radix-popover-content-available-width),calc(100vw-1rem))] overflow-y-auto overscroll-contain',
          )}
        >
          {children}
        </P.Content>
      </P.Portal>
    </P.Root>
  );
}

export function Tooltip({ content, children }: { content: ReactNode; children: ReactNode }) {
  return (
    <T.Provider delayDuration={300}>
      <T.Root>
        <T.Trigger asChild>{children}</T.Trigger>
        <T.Portal>
          <T.Content sideOffset={4} collisionPadding={COLLISION_PADDING} className="z-50 max-w-[min(20rem,var(--radix-tooltip-content-available-width))] rounded-md bg-slate-900 px-2 py-1 text-xs break-words text-white shadow-pop">
            {content}
          </T.Content>
        </T.Portal>
      </T.Root>
    </T.Provider>
  );
}
